import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { searchAPI } from '../../services/api';

const DEBOUNCE_MS = 280;
const MIN_LENGTH = 2;
const LIMIT = 10;

interface CompanyResult {
  id: string;
  name: string;
  jurisdiction: string;
  registrationNumber?: string;
}

interface PersonResult {
  id: string;
  name: string;
  email?: string;
  types?: string[];
}

interface SearchResponse {
  companies: CompanyResult[];
  people: PersonResult[];
}

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (q: string) => {
    if (!q || q.length < MIN_LENGTH) {
      setData(null);
      setOpen(!!q);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await searchAPI.search({ q: q.trim(), limit: LIMIT });
      setData(res.data);
      setOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Search failed');
      setData(null);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setData(null);
      setOpen(false);
      setError(null);
      return;
    }
    if (query.trim().length < MIN_LENGTH) {
      setOpen(true);
      setData(null);
      setError(null);
      return;
    }
    setOpen(true);
    debounceRef.current = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelectCompany = (id: string) => {
    navigate(`/companies/${id}`);
    setQuery('');
    setOpen(false);
  };

  const handleSelectPerson = (id: string) => {
    navigate(`/persons/${id}`);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || !open || !data) return;
    const firstCompany = data.companies?.[0];
    const firstPerson = data.people?.[0];
    if (firstCompany) {
      handleSelectCompany(firstCompany.id);
    } else if (firstPerson) {
      handleSelectPerson(firstPerson.id);
    }
  };

  const hasResults = !loading && data && (data.companies.length > 0 || data.people.length > 0);
  const showEmpty = open && !loading && query.trim().length >= MIN_LENGTH && !(data && (data.companies.length > 0 || data.people.length > 0)) && !error;
  const showHint = open && !loading && query.trim().length > 0 && query.trim().length < MIN_LENGTH;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'relative', width: { xs: 160, sm: 220, md: 280 } }}>
        <TextField
          inputRef={inputRef}
          size="small"
          placeholder="Search companies, people…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim().length >= MIN_LENGTH && data !== undefined && setOpen(true)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'inherit', opacity: 0.8 }} />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} color="inherit" />
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: 1,
              '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.8)' },
            },
            '& .MuiInputBase-input': { color: 'white', py: 1 },
            '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 },
          }}
          inputProps={{
            'aria-label': 'Search companies and people',
          }}
        />
        <Popper
          open={open}
          anchorEl={inputRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
          ref={popperRef}
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        >
          <Paper elevation={8} sx={{ maxHeight: 360, width: Math.max(inputRef.current?.offsetWidth ?? 280, 280), overflow: 'auto' }}>
            {showHint && (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Type at least {MIN_LENGTH} characters to search
                </Typography>
              </Box>
            )}
            {loading && (
              <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Searching…
                </Typography>
              </Box>
            )}
            {error && (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </Box>
            )}
            {showEmpty && (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No results found
                </Typography>
              </Box>
            )}
            {hasResults && (
              <List dense disablePadding>
                {data!.companies.length > 0 && (
                  <>
                    <Typography component="li" variant="overline" sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary', listStyle: 'none' }}>
                      Companies
                    </Typography>
                    {data!.companies.map((c) => (
                      <ListItemButton
                        key={c.id}
                        onClick={() => handleSelectCompany(c.id)}
                        sx={{ py: 0.5 }}
                      >
                        <BusinessIcon sx={{ mr: 1.5, color: 'action.active', fontSize: 20 }} />
                        <ListItemText
                          primary={c.name}
                          secondary={[c.jurisdiction, c.registrationNumber].filter(Boolean).join(' · ') || undefined}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    ))}
                  </>
                )}
                {data!.people.length > 0 && (
                  <>
                    <Typography component="li" variant="overline" sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary', listStyle: 'none' }}>
                      People
                    </Typography>
                    {data!.people.map((p) => (
                      <ListItemButton
                        key={p.id}
                        onClick={() => handleSelectPerson(p.id)}
                        sx={{ py: 0.5 }}
                      >
                        <PersonIcon sx={{ mr: 1.5, color: 'action.active', fontSize: 20 }} />
                        <ListItemText
                          primary={p.name}
                          secondary={p.email || (p.types?.length ? p.types.join(', ') : undefined)}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    ))}
                  </>
                )}
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
